import { DateFieldProps } from '@pelckmans/elementis/components/date-field';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ScorableItemTableDataGroup, ScorableItemTableDataRow } from '../../per-student/hooks/useTableData';
import { ListCreator } from './useScorableItemFilters.d';
import useScorableItemSelection from './useScorableItemSelection';
import { compose } from './utils';
import { useFuzzySearch } from '../../../../../../hooks/useFuzzySearch';

export type ScorableItemFilter = (item: ScorableItemTableDataRow) => boolean;

function countRowsInTree(
  group: ScorableItemTableDataGroup,
  countMap: Map<string, number>,
  filter?: ScorableItemFilter,
) {
  const filteredRowsCount = filter ? group.rows?.filter(filter).length || 0 : group.rows?.length || 0;

  const childGroupsRowsCount =
    group.groups?.reduce((acc, childGroup): number => {
      if (countMap.has(childGroup.id)) {
        return acc + countMap.get(childGroup.id)!;
      }

      const countForChildGroup = countRowsInTree(childGroup, countMap, filter);
      countMap.set(childGroup.id, countForChildGroup);
      return countForChildGroup + acc;
    }, 0) || 0;

  const rowsCountForGroup = filteredRowsCount + childGroupsRowsCount;

  countMap.set(group.id, rowsCountForGroup);
  return rowsCountForGroup;
}

function nodeTitleMatchesTitleFilter(
  group: ScorableItemTableDataGroup,
  groupTitleMatchesMap: Map<string, boolean>,
  titleFilter: (obj: { title: string }) => boolean,
): boolean {
  if (titleFilter(group.accumulatedRow)) {
    groupTitleMatchesMap.set(group.id, true);
    return true;
  }

  const childGroupsMatchesFilter =
    group.groups?.some(childGroup => nodeTitleMatchesTitleFilter(childGroup, groupTitleMatchesMap, titleFilter)) ||
    false;

  groupTitleMatchesMap.set(group.id, childGroupsMatchesFilter);
  return childGroupsMatchesFilter;
}

export const resultTypes = ['all', 'free-exercise', 'assignment', 'none'] as const;
export type ResultType = typeof resultTypes[number];

interface Data {
  tableDataGroups: ScorableItemTableDataGroup[];
  selectedItemIds?: string[];
}

function isRowGroup(rowGroup: ScorableItemTableDataGroup | null): rowGroup is ScorableItemTableDataGroup {
  return rowGroup !== null;
}

function isListCreator(creator: ListCreator | undefined): creator is ListCreator {
  return creator !== undefined;
}

function listWithTypeFilterCreatorFactory(countMap: Map<string, number>, typeFilter: ScorableItemFilter): ListCreator {
  countMap.clear();

  function mapScorableItemDataTableGroup(group: ScorableItemTableDataGroup): ScorableItemTableDataGroup | null {
    const subTreeRowsCount = countMap.has(group.id)
      ? countMap.get(group.id)!
      : countRowsInTree(group, countMap, typeFilter);

    if (subTreeRowsCount === 0) return null;

    return {
      ...group,
      rows: group.rows?.filter(typeFilter),
      groups: group.groups?.map(childGroup => mapScorableItemDataTableGroup(childGroup)).filter(isRowGroup),
    };
  }

  return tableDataGroups => {
    return tableDataGroups.map(mapScorableItemDataTableGroup).filter(isRowGroup);
  };
}

function listWithTitleFilterCreatorFactory(
  countMap: Map<string, number>,
  titleNodeMap: Map<string, boolean>,
  titleFilter: (obj: { title: string }) => boolean,
): ListCreator {
  countMap.clear();
  titleNodeMap.clear();

  function mapScorableItemDataTableGroup(group: ScorableItemTableDataGroup): ScorableItemTableDataGroup | null {
    if (titleFilter(group.accumulatedRow)) return group;

    const subTreeRowCount = countMap.has(group.id)
      ? countMap.get(group.id)!
      : countRowsInTree(group, countMap, titleFilter);

    const conformToTitleFilter = nodeTitleMatchesTitleFilter(group, titleNodeMap, titleFilter);

    if (subTreeRowCount === 0 && !conformToTitleFilter) return null;

    return {
      ...group,
      rows: group.rows?.filter(titleFilter),
      groups: group.groups?.map(childGroup => mapScorableItemDataTableGroup(childGroup)).filter(isRowGroup),
    };
  }

  return tableDataGroups => {
    return tableDataGroups.map(mapScorableItemDataTableGroup).filter(isRowGroup);
  };
}

function listWithDateRangeFilterCreatorFactory(
  countMap: Map<string, number>,
  dateFilter: (obj: Extract<ScorableItemTableDataRow, { type: 'assignment' }>) => boolean,
  allowedScorableItemType: ScorableItemTypeFilterConfig['value'],
): ListCreator | undefined {
  countMap.clear();

  function mapNode(group: ScorableItemTableDataGroup): ScorableItemTableDataGroup | null {
    const subTreeScorableItemCount = countMap.has(group.id)
      ? countMap.get(group.id)!
      : countRowsInTree(group, countMap, row => {
          // free-exercise do not need to match the date filter
          if (row.type === 'free-exercise') {
            return allowedScorableItemType === 'all';
          }

          return dateFilter(row);
        });

    if (subTreeScorableItemCount === 0) return null;

    return {
      ...group,
      rows: group.rows?.filter(row => {
        // free-exercise do not need to match the date filter
        if (row.type === 'free-exercise') {
          return allowedScorableItemType === 'all';
        }

        return dateFilter(row);
      }),
      groups: group.groups?.map(childGroup => mapNode(childGroup)).filter(isRowGroup),
    };
  }

  return tableDataGroups => {
    return tableDataGroups.map(mapNode).filter(isRowGroup);
  };
}

type ScorableItemTypeFilterConfig =
  | {
      value: Extract<ResultType, 'all'>;
      listCreator: undefined;
    }
  | {
      value: Extract<ResultType, 'free-exercise' | 'assignment' | 'none'>;
      listCreator: ListCreator;
    };

type TitleFilterConfig = {
  value: string;
  listCreator: ListCreator | undefined;
};

type DateFilterConfig = {
  value: Extract<DateFieldProps, { mode: 'range' }>['selected'];
  listCreator: ListCreator | undefined;
};

type SearchableItem = { id: string; title: string };

export default function useScorableItemFilters({ tableDataGroups, selectedItemIds }: Data) {
  const [scorableItemTypeFilterConfig, setScorableItemTypeFilterConfig] = useState<ScorableItemTypeFilterConfig>({
    value: 'all',
    listCreator: undefined,
  });

  const flattenedRows: SearchableItem[] = useMemo(() => {
    const result: SearchableItem[] = [];
    function recursiveSearch(groups: ScorableItemTableDataGroup[]) {
      groups.forEach(group => {
        if (group.accumulatedRow) result.push({ id: group.accumulatedRow.id, title: group.accumulatedRow.title });
        if (group.rows) result.push(...group.rows.map(r => ({ id: r.id, title: r.title })));
        if (group.groups) recursiveSearch(group.groups);
      });
    }
    recursiveSearch(tableDataGroups);
    return result;
  }, [tableDataGroups]);

  const fuzzySearchTitle = useFuzzySearch(flattenedRows, 'title');

  const [titleFilterConfig, setTitleFilterConfig] = useState<TitleFilterConfig>({
    value: '',
    listCreator: undefined,
  });

  const [dateFilterConfig, setDateFilterConfig] = useState<DateFilterConfig>({
    value: undefined,
    listCreator: undefined,
  });

  const [checkedScorableItemListCreator, setCheckedScorableItemListCreator] = useState<ListCreator | undefined>(
    undefined,
  );

  /**
   * These refs are used as optimizations for traversing the node tree
   */
  // map with count of scorable items per node, and its sub-nodes which comply with the type filter
  const scorableItemWithTypeFilterCountPerNode = useRef<Map<string, number>>(new Map());
  // map with count of scorable items per node, and its sub-nodes which comply with the title filter
  const scorableItemWithTitleFilterCountPerNode = useRef<Map<string, number>>(new Map());
  // map with boolean value indicating whether the node or any of its sub-nodes comply with the title filter
  const nodeOrSubNodesTitleFulfillmentPerNode = useRef<Map<string, boolean>>(new Map());
  // map with count of scorable items per node, and its sub-nodes, which comply to the date filter
  const scorableItemsWithDateFilterCountPerNode = useRef<Map<string, number>>(new Map());

  const changeDateFilters = useCallback(
    (value: DateFilterConfig['value'], allowedScorableItemType: ScorableItemTypeFilterConfig['value']) => {
      if (!value || allowedScorableItemType === 'free-exercise') {
        setDateFilterConfig({
          value: undefined,
          listCreator: undefined,
        });
        return;
      }

      const listCreator = listWithDateRangeFilterCreatorFactory(
        scorableItemsWithDateFilterCountPerNode.current,
        item => {
          return item.assignmentRanges.some(
            range =>
              (new Date(range.from).getTime() >= startOfDay(value.from!).getTime() &&
                new Date(range.from).getTime() <= endOfDay(value.to!).getTime()) ||
              (new Date(range.to).getTime() >= startOfDay(value.from!).getTime() &&
                new Date(range.to).getTime() <= endOfDay(value.to!).getTime()),
          );
        },
        allowedScorableItemType,
      );

      setDateFilterConfig({
        value,
        listCreator,
      });
    },
    [],
  );

  const changeScorableItemType = useCallback(
    (type: ResultType) => {
      if (type === scorableItemTypeFilterConfig.value) return;

      if (type === 'all') {
        setScorableItemTypeFilterConfig({ value: type, listCreator: undefined });
      } else if (type === 'none') {
        setScorableItemTypeFilterConfig({
          value: type,
          listCreator: (): ScorableItemTableDataGroup[] => [],
        });
      } else {
        const listCreator = listWithTypeFilterCreatorFactory(scorableItemWithTypeFilterCountPerNode.current, row => {
          const isAssignment = row.id.split('-')[1] === 'true';
          return (type === 'assignment' && isAssignment) || (type === 'free-exercise' && !isAssignment);
        });

        setScorableItemTypeFilterConfig({
          value: type,
          listCreator,
        });
      }

      changeDateFilters(dateFilterConfig.value, type);
    },
    [changeDateFilters, dateFilterConfig.value, scorableItemTypeFilterConfig.value],
  );

  const changeTitleFilterText = useCallback((filterText: string) => {
    setTitleFilterConfig(prev => ({
      ...prev,
      value: filterText,
    }));
  }, []);

  const changeTitleFilters = useCallback(
    (value: string) => {
      let listCreator: ListCreator | undefined;

      if (value) {
        const matchingRows = fuzzySearchTitle(value);

        listCreator = listWithTitleFilterCreatorFactory(
          scorableItemWithTitleFilterCountPerNode.current,
          nodeOrSubNodesTitleFulfillmentPerNode.current,
          ({ title }) => matchingRows.some(row => row.title === title),
        );
      }

      setTitleFilterConfig(prev => ({
        ...prev,
        listCreator,
      }));
    },
    [fuzzySearchTitle],
  );

  /**
   * this represents the list items which need to be rendered in the modal
   * its result is a combination of the scorable item type filter and the title filter
   * each filter has the responsibility to create a list of items that match its criteria
   */
  const filteredTableData = useMemo(() => {
    const creators = [
      scorableItemTypeFilterConfig.listCreator,
      titleFilterConfig.listCreator,
      dateFilterConfig.listCreator,
    ].filter(isListCreator);

    if (
      checkedScorableItemListCreator &&
      (scorableItemTypeFilterConfig.listCreator || titleFilterConfig.listCreator || dateFilterConfig.listCreator)
    ) {
      creators.push(checkedScorableItemListCreator);
    }

    if (creators.length === 0) return tableDataGroups;

    const creator = compose(...creators);

    return creator(tableDataGroups);
  }, [
    checkedScorableItemListCreator,
    dateFilterConfig.listCreator,
    tableDataGroups,
    scorableItemTypeFilterConfig.listCreator,
    titleFilterConfig.listCreator,
  ]);

  const clearFilters = useCallback(() => {
    changeTitleFilterText('');
    changeTitleFilters('');
    changeScorableItemType('all');
    changeDateFilters(undefined, 'all');
  }, [changeDateFilters, changeScorableItemType, changeTitleFilterText, changeTitleFilters]);

  const selection = useScorableItemSelection({
    filteredTableDataGroups: filteredTableData,
    tableDataGroups: tableDataGroups,
    setListCreator: setCheckedScorableItemListCreator,
    selectedItemIds,
  });

  let filterActive = false;
  if (titleFilterConfig.listCreator !== undefined) filterActive = true;
  else if (scorableItemTypeFilterConfig.value !== 'all') filterActive = true;
  else if (scorableItemTypeFilterConfig.value === 'all' && dateFilterConfig.value) filterActive = true;

  return {
    filterActive,
    scorableItemType: scorableItemTypeFilterConfig.value,
    titleFilterText: titleFilterConfig.value,
    setTitleFilterText: changeTitleFilterText,
    setTitleFilters: changeTitleFilters,
    setScorableItemType: changeScorableItemType,
    setDateRange: changeDateFilters,
    clearFilters,
    listItems: filteredTableData,
    ...selection,
  };
}
